// apps/web/src/api/aiImport.ts
// AI 导题 API 调用 — 整体异步任务模式

import api from "./client";
import type { CreateQuestionRequest } from "@examfast/shared-types";

export interface AIImportOptions {
  maxQuestions?: number;
  defaultDifficulty?: number;
}

export interface AIExtractedQuestion extends CreateQuestionRequest {
  _raw?: string;
}

export type AIJobStatus = "processing" | "completed" | "failed";
// 进度基础阶段。服务器也可能返回 "ai_extracting:1/5" 这类动态进度，前端需解析。
export type AIJobProgress =
  | "parsing"
  | "creating_bank"
  | "ai_extracting"
  | "writing"
  | "completed"
  | "failed";

export interface AIImportJob {
  jobId: string;
  status: AIJobStatus;
  /** 进度阶段，可能是基础阶段，也可能是 "ai_extracting:1/5" 这类动态进度 */
  progress?: string | null;
  fileName: string;
  fileSize: number;
  fileType: string | null;
  bankId: string | null;
  bankName: string | null;
  questionCount: number;
  successCount: number;
  failCount: number;
  /** AI 识别阶段的流式日志（最近几千字符） */
  logs?: string | null;
  createdAt: number;
  updatedAt: number;
  result?: {
    questions?: AIExtractedQuestion[];
    total?: number;
    usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
    bankId?: string;
    bankName?: string;
    successCount?: number;
    failCount?: number;
    failures?: { index: number; content: string; reason: string }[];
  } | null;
  error?: string | null;
}

export interface AIImportRequest {
  text: string;
  fileName: string;
  fileSize?: number;
  fileType?: string;
  options?: AIImportOptions;
}

export interface AIImportStartResult {
  jobId: string;
  bankName: string;
}

/** 创建 AI 导入任务（识别 + 自动建题库 + 导入），立即返回 jobId */
export async function aiImportStartApi(data: AIImportRequest): Promise<AIImportStartResult> {
  return api.post<AIImportStartResult>("ai/import", data);
}

/** 轮询任务状态 */
export async function aiImportJobStatusApi(jobId: string): Promise<AIImportJob> {
  return api.get<AIImportJob>(`ai/import/jobs/${jobId}`);
}

/** 列出当前用户的导入任务 */
export async function aiImportListJobsApi(page = 1, pageSize = 20): Promise<{
  items: AIImportJob[];
  total: number;
  page: number;
  page_size: number;
}> {
  return api.get<{ items: AIImportJob[]; total: number; page: number; page_size: number }>(
    `ai/import/jobs?page=${page}&page_size=${pageSize}`
  );
}

/** 删除导入记录 */
export async function aiImportDeleteJobApi(jobId: string): Promise<void> {
  await api.del(`ai/import/jobs/${jobId}`);
}
